/**
 * Draws the page-transition logo on an OffscreenCanvas, off the main
 * thread. The worker has its own event loop and frame callbacks, and a
 * transferred canvas commits frames straight to the compositor, so the
 * draw keeps going while the main thread is busy mounting the next page
 * (R3F scene, GLB parsing, shader compiles), which used to freeze it.
 */
import { LogoLoop, clearLogo, drawLogoFrame, toEpochMs, type LogoTheme } from "./logoFrame";

export type LogoWorkerMessage =
    | { type: "init"; canvas: OffscreenCanvas; cssSize: number; dpr: number; subpathLengths: number[] }
    | { type: "resize"; cssSize: number; dpr: number; endScale: number }
    | { type: "start"; theme: LogoTheme; endScale: number }
    | { type: "finish"; id: number }
    | { type: "reset" };

/** `finishing`: the answer to `finish`, straight away: when (epoch ms,
 *  see `toEpochMs`) the logo will have come to rest fully formed.
 *  `done`: it has.
 *  `unsupported`: the canvas gave no 2D context, so the page falls back to
 *  drawing on the main thread instead of waiting on a blank worker. */
export type LogoWorkerReply =
    | { type: "finishing"; id: number; restsAtEpochMs: number }
    | { type: "done"; id: number }
    | { type: "unsupported" };

// Typed as the bits of DedicatedWorkerGlobalScope used here (the app's
// tsconfig uses the DOM lib, which doesn't declare worker globals).
const scope = self as unknown as {
    onmessage: ((e: MessageEvent<LogoWorkerMessage>) => void) | null;
    postMessage: (msg: LogoWorkerReply) => void;
    requestAnimationFrame?: (cb: () => void) => number;
};

// Workers in some browsers have no requestAnimationFrame.
const nextFrame = (cb: () => void) =>
    scope.requestAnimationFrame ? scope.requestAnimationFrame(cb) : setTimeout(cb, 16);

let canvas: OffscreenCanvas | null = null;
let ctx: OffscreenCanvasRenderingContext2D | null = null;
let subpathLengths: number[] = [];
let cssSize = 0;
let dpr = 1;

let loop: LogoLoop | null = null;
/** When `loop` was last advanced to (`performance.now()` ms). */
let last = 0;
/** How the loop in flight is drawn; `endScale` follows the breakpoint. */
let look: { theme: LogoTheme; endScale: number } | null = null;
/** `finish` id to answer once the loop rests. A finish can arrive before
 *  its start (fast page), so it's kept until the next loop picks it up. */
let finishId: number | null = null;
/** Bumped by every start/reset so a stale frame loop stops itself. */
let generation = 0;

function size(nextCssSize: number, nextDpr: number) {
    cssSize = nextCssSize;
    dpr = nextDpr;
    if (canvas) {
        canvas.width = Math.round(cssSize * dpr);
        canvas.height = Math.round(cssSize * dpr);
    }
}

/** Draws the loop's current state, e.g. after a resize cleared the canvas
 *  while it rests on the formed logo and no frame is coming. */
function redraw() {
    if (ctx && loop && look) drawLogoFrame(ctx, loop.state, { subpathLengths, ...look, cssSize, dpr });
}

/** Brings the loop up to now. */
function tick() {
    if (!loop) return;
    const now = performance.now();
    loop.advance(now - last);
    last = now;
}

/** Answers the pending `finish` with when the loop will rest. */
function announceFinish(current: LogoLoop) {
    if (finishId === null || current.restsInMs === null) return;
    scope.postMessage({ type: "finishing", id: finishId, restsAtEpochMs: toEpochMs(performance.now() + current.restsInMs) });
}

function run(theme: LogoTheme, endScale: number) {
    const gen = ++generation;
    look = { theme, endScale };
    const current = (loop = new LogoLoop());
    last = performance.now();
    if (finishId !== null) {
        current.finish();
        announceFinish(current);
    }

    const frame = () => {
        if (gen !== generation || !ctx) return;
        tick();
        redraw();
        if (current.done && finishId !== null) {
            scope.postMessage({ type: "done", id: finishId });
            finishId = null;
            return; // rest on the formed logo
        }
        nextFrame(frame);
    };
    frame();
}

scope.onmessage = (e) => {
    const msg = e.data;
    switch (msg.type) {
        case "init":
            canvas = msg.canvas;
            ctx = canvas.getContext("2d");
            if (!ctx) scope.postMessage({ type: "unsupported" });
            subpathLengths = msg.subpathLengths;
            size(msg.cssSize, msg.dpr);
            break;
        case "resize":
            size(msg.cssSize, msg.dpr);
            if (look) look.endScale = msg.endScale;
            redraw();
            break;
        case "start":
            if (ctx) run(msg.theme, msg.endScale);
            break;
        case "finish":
            finishId = msg.id;
            if (loop) {
                tick();
                loop.finish();
                announceFinish(loop);
                // Already at rest (no frame loop left to answer).
                if (loop.done) {
                    scope.postMessage({ type: "done", id: finishId });
                    finishId = null;
                }
            }
            break;
        case "reset":
            generation++;
            loop = null;
            look = null;
            finishId = null;
            if (ctx) clearLogo(ctx);
            break;
    }
};
